import React from "react"
import PropTypes from "prop-types"
import FontAwesome from "react-fontawesome"
import Fuse from "fuse.js"
import moment from "moment"
import CSVButton from "@swipesense/csv-button"

import { latestUpdatedTime } from "../../helpers/searchAssets"
import { fuseConfig } from "../../helpers/sharedHelperFunctions"

import ResultCount from "../ResultCount"
import Loading from "../Loading"
import DepartmentDropdownWithData from "../DepartmentDropdown/DepartmentDropdownWithData"
import SearchBox from "../SearchBox"

import LowBatteryTableVirtualized from "./LowBatteryTableVirtualized"
import "./LowBatteryReport.scss"

const sortLowBattery = (assetA, assetB) => {
  if (assetA.item.batteryStatus > assetB.item.batteryStatus) {
    return -1
  }
  if (assetA.item.batteryStatus < assetB.item.batteryStatus) {
    return 1
  }
  return 0
}

const determineLowBattery = assets => {
  return assets.filter(asset => {
    if (!asset.lastSeenAt || !asset.location || !asset.batteryStatus) {
      return false
    }
    return asset.batteryStatus !== "good"
  })
}

const searchLowBattery = (searchTerms, assets) => {
  if (!searchTerms) {
    return assets.map(asset => ({
      item: asset,
    }))
  }
  const fuse = new Fuse(assets, {
    ...fuseConfig,
    keys: [
      "name",
      "assetId",
      "labels",
      "manufacturer",
      "modelName",
      "location.name",
    ],
  })
  return fuse.search(searchTerms)
}

const csvHeaders = [
  { label: "Asset", key: "name" },
  { label: "Manufacturer", key: "manufacturer" },
  { label: "Model", key: "modelName" },
  { label: "Asset ID", key: "assetId" },
  { label: "Serial Number", key: "serialNumber" },
  { label: "Department", key: "ownerNames" },
  { label: "Ownership", key: "ownership" },
  { label: "Last Location", key: "locationName" },
  { label: "Last Seen", key: "timeLost" },
  { label: "Battery Status", key: "batteryStatus" },
]

const csvDataMap = lowBattery => ({
  ...lowBattery.item,
  locationName: [
    lowBattery.item.location.unit.name,
    lowBattery.item.location.name,
  ].join(" "),
  ownerNames: lowBattery.item.owners
    .map(owner => owner.name.trim())
    .join(" and "),
  timeLost: moment(lowBattery.item.lastSeenAt).format("HH:MM MM/DD/YYYY"),
})

class LowBatteryReport extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      filterSearch: "",
      filterToRentals: false,
      filteredDept: null,
    }
  }

  updateDepartment = departmentSelection => {
    this.setState({
      filteredDept: departmentSelection,
    })
  }

  render() {
    const formattedUpdateTime = this.props.loading
      ? null
      : latestUpdatedTime(this.props.allAssets)

    const availableAssets = this.props.allAssets
      .filter(
        asset => !this.state.filterToRentals || asset.ownership === "rented"
      )
      .filter(
        asset =>
          this.state.filteredDept == null ||
          this.state.filteredDept.value == null ||
          asset.owners.map(o => o.id).includes(this.state.filteredDept.value)
      )
    const lowBatteryAssets = searchLowBattery(
      this.state.filterSearch,
      determineLowBattery(availableAssets)
    ).sort(sortLowBattery)

    const csvFileName = `SwipeSense Low Battery Tag Report ${
      this.state.filteredDept ? this.state.filteredDept.label.trim() + " " : ""
    }${
      this.state.filterToRentals ? "Rentals " : ""
    }${new Date().toLocaleDateString()}.csv`

    return (
      <div className="low-battery-assets-report-container">
        <div>
          {this.props.loading ? (
            <Loading />
          ) : (
            <div className="LowBatteryReport">
              <div className="low-battery-top-bar">
                <div className="low-battery-summary">
                  TAGS TO REPLACE
                  <div className="inventory-numbers">
                    <div className="inventory-percent">
                      <b>
                        {Math.round(
                          (lowBatteryAssets.length / availableAssets.length) *
                            100
                        ) || 0}
                        %
                      </b>
                    </div>
                    <div className="inventory-fraction">
                      ({lowBatteryAssets.length}/{availableAssets.length})
                    </div>
                  </div>
                </div>
                <div className="filter-options">
                  <label className="rentals-only" htmlFor="onlyRented">
                    <input
                      type="checkbox"
                      onChange={event => {
                        this.setState({
                          filterToRentals: event.target.checked,
                        })
                      }}
                      name={"onlyRented"}
                      id={"onlyRented"}
                      checked={this.state.filterToRentals}
                    />
                    <FontAwesome className="rental-tag" name={"tag"} />
                    Rentals only
                  </label>
                  <DepartmentDropdownWithData
                    className="department-dropdown"
                    facilityId={this.props.facilityId}
                    departmentUpdated={this.updateDepartment}
                  />
                </div>
              </div>
              <div className="report-controls">
                <ResultCount
                  resultCount={lowBatteryAssets.length}
                  deviceType={this.props.deviceType}
                  loading={this.props.loading}
                  updateTime={formattedUpdateTime}
                />
                <div className="search-download">
                  <SearchBox
                    query={this.state.filterSearch}
                    onChange={query => this.setState({ filterSearch: query })}
                  />
                  <CSVButton
                    data={lowBatteryAssets}
                    filename={csvFileName}
                    headers={csvHeaders}
                    mapData={csvDataMap}
                  />
                </div>
              </div>
              <div className="results">
                <LowBatteryTableVirtualized
                  lowBatteryAssets={lowBatteryAssets}
                  deviceType={this.props.deviceType}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
}

LowBatteryReport.propTypes = {
  facilityId: PropTypes.string.isRequired,
  allAssets: PropTypes.array,
  loading: PropTypes.bool,
  deviceType: PropTypes.string.isRequired,
}

LowBatteryReport.defaultProps = {
  allAssets: [],
  deviceType: "desktop",
}

export default LowBatteryReport
